import { useState } from "react";
import { clsx } from "clsx";
import { XIcon } from "@phosphor-icons/react";
import { ICON_SIZE } from "@/config/constants";
import { Button } from "@heroui/react";

export interface ColorDefinition {
  label: string;
  bgClass: string;
  fgClass: string;
}

interface ColorPickerProps<T extends string = string> {
  colors: Record<T, ColorDefinition>;
  selectedColor?: T | null;
  onChange?: (color: T | null) => void;
  label?: string;
}

export function ColorPicker<T extends string = string>({
  colors,
  selectedColor,
  onChange,
  label = "Select color",
}: ColorPickerProps<T>) {
  const colorEntries = Object.entries(colors) as [T, ColorDefinition][];
  const defaultColor = colorEntries[0]?.[0] || "red";
  const [internalColor, setInternalColor] = useState<T | string | null>(selectedColor || defaultColor);

  const handleColorChange = (color: T | null) => {
    setInternalColor(color);
    onChange?.(color);
  };

  const displayColor = selectedColor !== undefined ? selectedColor : internalColor;

  return (
    <fieldset className="text-default-500 flex flex-col items-center border-0 p-0">
      <legend className="mb-2 text-sm font-medium">{label}</legend>
      <div className="flex items-center gap-2">
        <div className="grid grid-cols-6 gap-3">
          {colorEntries.map(([colorName, { label: colorLabel, bgClass }]) => (
            <Button
              aria-label={colorLabel}
              aria-pressed={displayColor === colorName}
              className={clsx(
                "h-7 min-h-7 w-7 min-w-7",
                bgClass,
                displayColor === colorName ? "border-2" : "hover:scale-105 hover:opacity-90"
              )}
              isIconOnly
              radius="sm"
              key={colorName}
              onPress={() => handleColorChange(colorName)}
            />
          ))}
          <Button
            aria-label="Clear"
            aria-pressed={displayColor === null}
            className={clsx(
              "bg-background h-7 min-h-7 w-7 min-w-7",
              displayColor === null ? "border-primary border-2" : "hover:scale-105 hover:opacity-90"
            )}
            isIconOnly
            radius="sm"
            variant="bordered"
            onPress={() => handleColorChange(null)}
          >
            <XIcon size={ICON_SIZE.md} />
          </Button>
        </div>
      </div>
    </fieldset>
  );
}
